import { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, setDoc, addDoc, Timestamp, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { colors, LoadingSpinner, Card, Button, EmptyState } from '../../shared';
import { Plus, Edit2, Clock, Play, Eye, Archive, ChevronRight, Search, Filter, Copy, Trash2, FileText } from 'lucide-react';
import PromptEditor from '../prompts/PromptEditor';
import PromptVersionHistory from '../prompts/PromptVersionHistory';
import PromptTestSandbox from '../prompts/PromptTestSandbox';

const ProductBadge = ({ product }) => {
  const productColors = {
    'trades-app': colors.yellow,
    'insurance-estimator': colors.green,
  };
  const color = productColors[product] || colors.textMuted;

  return (
    <span
      className="px-2 py-0.5 rounded text-xs font-medium"
      style={{ background: `${color}20`, color }}
    >
      {product}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const statusColors = {
    active: colors.green,
    testing: colors.orange,
    deprecated: colors.red,
  };
  const color = statusColors[status] || colors.textMuted;

  return (
    <span
      className="px-2 py-0.5 rounded text-xs font-medium"
      style={{ background: `${color}20`, color }}
    >
      {status}
    </span>
  );
};

const PromptCard = ({ prompt, onClick, onTest }) => {
  const successRate = prompt.stats?.successRate || 0;
  const totalCalls = prompt.stats?.totalCalls || 0;

  return (
    <Card
      className="cursor-pointer hover:border-opacity-50 transition-all"
      style={{ border: `1px solid ${colors.border}` }}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold" style={{ color: colors.textPrimary }}>{prompt.name}</h3>
            <StatusBadge status={prompt.status} />
          </div>
          <p className="text-xs font-mono" style={{ color: colors.textMuted }}>{prompt.id}</p>
        </div>
        <ProductBadge product={prompt.product} />
      </div>

      <div className="flex items-center gap-4 text-xs" style={{ color: colors.textSecondary }}>
        <span>v{prompt.currentVersion || 1}</span>
        <span>{totalCalls.toLocaleString()} calls</span>
        <span>{(successRate * 100).toFixed(0)}% success</span>
        <span>{prompt.model}</span>
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onTest(prompt); }}
            className="p-2 rounded-lg hover:bg-opacity-50 transition-all"
            style={{ background: colors.yellowDim, color: colors.yellow }}
            title="Test prompt"
          >
            <Play size={14} />
          </button>
        </div>
        <ChevronRight size={16} style={{ color: colors.textMuted }} />
      </div>
    </Card>
  );
};

export default function PromptsTab({ permissions }) {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProduct, setFilterProduct] = useState('all');
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [testingPrompt, setTestingPrompt] = useState(null);

  const canEdit = permissions?.editPrompts;
  const canDeploy = permissions?.deployPrompts;

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'ai_prompts'));
      const promptsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPrompts(promptsData);
    } catch (error) {
      console.error('Error loading prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPrompts = prompts.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          p.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProduct = filterProduct === 'all' || p.product === filterProduct;
    return matchesSearch && matchesProduct;
  });

  const handleSavePrompt = async (promptData, isNew) => {
    try {
      if (isNew) {
        await setDoc(doc(db, 'ai_prompts', promptData.id), {
          ...promptData,
          currentVersion: 1,
          stats: { totalCalls: 0, successRate: 0, avgLatencyMs: 0, lastUsed: null },
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      } else {
        await setDoc(doc(db, 'ai_prompts', promptData.id), {
          ...promptData,
          updatedAt: Timestamp.now(),
        }, { merge: true });
      }
      await loadPrompts();
      setShowEditor(false);
      setSelectedPrompt(null);
    } catch (error) {
      console.error('Error saving prompt:', error);
      throw error;
    }
  };

  const handleCreateVersion = async (promptId, versionData) => {
    try {
      const promptRef = doc(db, 'ai_prompts', promptId);
      const promptSnap = await getDoc(promptRef);
      const currentVersion = promptSnap.data()?.currentVersion || 1;
      const newVersion = currentVersion + 1;

      // Create version record
      await addDoc(collection(db, 'ai_prompt_versions'), {
        promptId,
        version: newVersion,
        ...versionData,
        status: 'testing',
        createdAt: Timestamp.now(),
        performanceSnapshot: { totalCalls: 0, successRate: 0, feedbackScore: 0 },
      });

      // Update prompt with new version
      await setDoc(promptRef, {
        currentVersion: newVersion,
        ...versionData,
        updatedAt: Timestamp.now(),
      }, { merge: true });

      await loadPrompts();
    } catch (error) {
      console.error('Error creating version:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (showEditor && selectedPrompt) {
    return (
      <PromptEditor
        prompt={selectedPrompt}
        isNew={selectedPrompt.isNew}
        onSave={handleSavePrompt}
        onCancel={() => { setShowEditor(false); setSelectedPrompt(null); }}
        onCreateVersion={handleCreateVersion}
        canEdit={canEdit}
        canDeploy={canDeploy}
      />
    );
  }

  if (showVersionHistory && selectedPrompt) {
    return (
      <PromptVersionHistory
        prompt={selectedPrompt}
        onBack={() => { setShowVersionHistory(false); setSelectedPrompt(null); }}
        onDeploy={canDeploy ? handleCreateVersion : null}
      />
    );
  }

  if (testingPrompt) {
    return (
      <PromptTestSandbox
        prompt={testingPrompt}
        onBack={() => setTestingPrompt(null)}
      />
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>AI Prompts</h1>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            Manage and version your AI prompts
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={() => {
              setSelectedPrompt({
                isNew: true,
                id: '',
                name: '',
                product: 'trades-app',
                systemPrompt: '',
                userPromptTemplate: '',
                templateVariables: [],
                model: 'gpt-4o-mini',
                maxTokens: 1000,
                temperature: 0.7,
                outputFormat: 'text',
                jsonSchema: null,
                status: 'testing',
              });
              setShowEditor(true);
            }}
          >
            <Plus size={18} />
            New Prompt
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
          <input
            type="text"
            placeholder="Search prompts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl outline-none"
            style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
          />
        </div>
        <select
          value={filterProduct}
          onChange={(e) => setFilterProduct(e.target.value)}
          className="px-4 py-2 rounded-xl outline-none"
          style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
        >
          <option value="all">All Products</option>
          <option value="trades-app">Trades App</option>
          <option value="insurance-estimator">Insurance Estimator</option>
        </select>
      </div>

      {/* Prompts Grid */}
      {filteredPrompts.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No prompts found"
          description={searchTerm ? 'Try adjusting your search' : 'Create your first AI prompt'}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredPrompts.map(prompt => (
            <PromptCard
              key={prompt.id}
              prompt={prompt}
              onClick={() => {
                setSelectedPrompt(prompt);
                setShowEditor(true);
              }}
              onTest={(p) => setTestingPrompt(p)}
            />
          ))}
        </div>
      )}

    </div>
  );
}
